import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  Tabs,
  Tab,
  Alert,
  CircularProgress
} from '@mui/material';
import { Close as CloseIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';

/**
 * ThemePreviewDialog – shared by Quick Theme (DatabaseMode) and Custom Theme flows.
 *
 * Props:
 *   open          {boolean}  – controls dialog visibility
 *   onClose       {function} – called when user closes/cancels
 *   onGenerate    {function} – called when user clicks "Generate Theme"
 *   previewData   {object}   – { pages: { [filename]: htmlString }, profileUsed, profileName }
 *   previewLoading {boolean} – true while API call is in-flight
 *   previewError  {string}   – error message, or null
 *   title         {string}   – optional dialog header (default: "Theme Preview")
 */
const ThemePreviewDialog = ({
  open,
  onClose,
  onGenerate,
  previewData,
  previewLoading,
  previewError,
  title = 'Theme Preview'
}) => {
  const pageKeys = previewData && previewData.pages ? Object.keys(previewData.pages) : [];
  const [currentTab, setCurrentTab] = useState(0);

  // Reset to first tab whenever new preview data arrives
  useEffect(() => {
    setCurrentTab(0);
  }, [previewData]);

  const safeTab = pageKeys.length > 0 ? Math.max(0, Math.min(currentTab, pageKeys.length - 1)) : 0;
  const currentPageKey = pageKeys[safeTab] || '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: '90vw',
          height: '85vh',
          maxWidth: 'none',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: 2,
        borderBottom: '1px solid #e5e7eb',
        flexShrink: 0
      }}>
        <Typography variant="h6" fontWeight="bold">
          {title}
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="Close preview">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <DialogContent sx={{ p: 0, flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {previewLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, flexDirection: 'column', gap: 2 }}>
            <CircularProgress size={48} />
            <Typography color="text.secondary">Generating preview…</Typography>
          </Box>
        )}

        {!previewLoading && previewError && (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">{previewError}</Alert>
          </Box>
        )}

        {!previewLoading && !previewError && pageKeys.length === 0 && (
          <Box sx={{ p: 3 }}>
            <Alert severity="info">No pages available for preview. Please try again.</Alert>
          </Box>
        )}

        {!previewLoading && !previewError && pageKeys.length > 0 && (
          <>
            {/* Page Tabs */}
            <Tabs
              value={safeTab}
              onChange={(_, v) => setCurrentTab(v)}
              sx={{ borderBottom: '1px solid #e5e7eb', flexShrink: 0, px: 2 }}
              variant="scrollable"
              scrollButtons="auto"
            >
              {pageKeys.map((key) => (
                <Tab
                  key={key}
                  label={key.replace('.php', '').replace(/-/g, ' ')}
                  sx={{ textTransform: 'capitalize' }}
                />
              ))}
            </Tabs>

            {/* iframe Preview */}
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <iframe
                key={currentPageKey}
                srcDoc={previewData.pages[currentPageKey]}
                title={`Preview of ${currentPageKey}`}
                style={{ width: '100%', height: '100%', border: 'none' }}
              />
            </Box>
          </>
        )}
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb', gap: 1, flexShrink: 0 }}>
        {previewData && previewData.profileUsed && (
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            ✓ Using profile: {previewData.profileName}
          </Typography>
        )}
        <Button
          onClick={onClose}
          startIcon={<ArrowBackIcon />}
          sx={{ color: '#6b7280', textTransform: 'none' }}
        >
          Make Changes
        </Button>
        <Button
          onClick={() => { onClose(); onGenerate(); }}
          variant="contained"
          disabled={previewLoading || !!previewError}
          sx={{
            background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            '&:hover': { background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)' }
          }}
        >
          Generate Theme
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ThemePreviewDialog;
